/**
 * @module NotificationBell
 * @package @geenius/notifications-solidjs
 * @description Renders the bell trigger used to open the notification panel in
 * the Tailwind SolidJS variant.
 */

import { motion } from '@geenius/motion/solidjs'
import type { JSX } from 'solid-js'
import { Show } from 'solid-js'
import { createNotificationCopy } from '../lib/notificationCopy'
import { cn } from '../lib/utils'

/**
 * Props for the SolidJS notification bell trigger.
 */
export interface NotificationBellProps {
  unreadCount?: number
  onClick?: () => void
  isOpen?: boolean
  ariaControls?: string
  class?: string
}

/**
 * Renders the bell button used to toggle the notification panel.
 *
 * @param props Bell props including unread count and optional open state.
 * @returns The bell trigger element.
 */
export function NotificationBell(props: NotificationBellProps): JSX.Element {
  const { plural, t } = createNotificationCopy()
  const count = () => props.unreadCount ?? 0
  const accessibleCount = () =>
    count() > 99
      ? t('notifications.bell.countOverflowAria')
      : plural('notifications.bell.countAria', count())

  return (
    <motion.button
      type="button"
      onClick={() => props.onClick?.()}
      aria-label={count() > 0 ? accessibleCount() : t('notifications.bell.openAria')}
      aria-haspopup="dialog"
      aria-expanded={props.isOpen ?? false}
      aria-controls={props.ariaControls}
      whileHover={{ scale: 1.05, y: -1 }}
      whileTap={{ scale: 0.96 }}
      class={cn(
        'relative rounded-full p-2 text-muted-foreground transition-colors hover:bg-[var(--gn-notifications-panel-surface-muted)] hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        props.class,
      )}
    >
      <svg
        aria-hidden="true"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      <Show when={count() > 0}>
        <span
          aria-hidden="true"
          class="absolute right-0 top-0 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full border border-border bg-destructive px-1 text-[10px] font-bold text-white shadow-[0_0_8px_var(--gn-notifications-badge-glow)]"
        >
          {count() > 99 ? '99+' : count()}
        </span>
      </Show>
    </motion.button>
  )
}
